/**
 * Projects/boards command handlers
 */

import { readFileSync } from 'fs';
import { createProjectService, createSectionService, createBoardService, getApiUrl } from '../utils/client.js';
import { chalk, formatOutput, formatProjectTable, formatPagination, formatSuccess, formatInfo, formatTable, isInteractiveOutput } from '../utils/format.js';
import { handleApiError, parseNumber } from '../utils/errors.js';
import { cliDescriptionToHtml } from '../../../../src/utils/cli-rich-text.js';
import type { CreateBoardInput } from '../../../../src/validations/project.validation.js';
import type { ProjectMember, ProjectAgent } from '../../../../src/lib/services/project.service.js';

type SectionListEntry = {
  id: number;
  section_title: string;
  title?: string;
};

function isProjectAgent(m: ProjectMember | ProjectAgent): m is ProjectAgent {
  return 'owner' in m;
}

function normalizeCreateBoardManifest(manifest: CreateBoardInput): CreateBoardInput {
  return {
    ...manifest,
    ...(manifest.description !== undefined
      ? { description: cliDescriptionToHtml(manifest.description) }
      : {}),
    ...(manifest.tasks !== undefined
      ? {
          tasks: manifest.tasks.map((t) =>
            t.description !== undefined
              ? { ...t, description: cliDescriptionToHtml(t.description) }
              : t
          ),
        }
      : {}),
  };
}

async function readStdinUtf8(): Promise<string> {
  if (process.stdin.isTTY) {
    throw new Error('stdin is a TTY; pipe JSON in or use --manifest <file>');
  }
  const chunks: Buffer[] = [];
  for await (const chunk of process.stdin) {
    chunks.push(Buffer.from(chunk));
  }
  return Buffer.concat(chunks).toString('utf8').trim();
}

/**
 * Create a board from a JSON manifest (parity with MCP create_board).
 */
export async function createBoardFromManifest(options: {
  manifestPath?: string;
  stdin: boolean;
  token?: string;
  apiUrl?: string;
  json?: boolean;
}): Promise<void> {
  try {
    let raw: string;
    if (options.stdin) {
      raw = await readStdinUtf8();
    } else if (options.manifestPath) {
      raw = readFileSync(options.manifestPath, 'utf8');
    } else {
      throw new Error('Provide --manifest <file.json> or --stdin');
    }

    const parsed = JSON.parse(raw) as CreateBoardInput;
    const params = normalizeCreateBoardManifest(parsed);
    const service = await createBoardService(options.token, options.apiUrl);
    const result = await service.createBoard(params);

    if (options.json) {
      console.log(formatOutput(result, true));
    } else {
      const b = result.board;
      console.log(formatSuccess(`Created board "${b.title}" (id: ${b.id})`));
      console.log(
        formatInfo(
          `team_id ${result.team_id} · sections ${result.sections?.length ?? 0} · labels ${result.labels?.length ?? 0} · tasks ${result.tasks?.length ?? 0}`
        )
      );
      if (result.board?.link?.url) {
        console.log(formatInfo(result.board.link.url));
      }
    }
  } catch (error) {
    handleApiError(error);
  }
}

export async function listProjects(
  token?: string,
  apiUrl?: string,
  options?: { limit?: number; offset?: number; json?: boolean }
): Promise<void> {
  try {
    const service = await createProjectService(token, apiUrl);
    const result = await service.listProjects({
      limit: options?.limit || 10,
      offset: options?.offset || 0,
    });

    if (options?.json) {
      console.log(formatOutput(result, true));
    } else {
      if (result.projects && result.projects.length > 0) {
        const appBase = new URL(getApiUrl(apiUrl)).origin;
        if (isInteractiveOutput()) {
          console.log('');
          console.log(chalk.bold.white('  Projects'));
        }
        console.log(formatProjectTable(result.projects, appBase));

        if (result.has_more && isInteractiveOutput()) {
          console.log(
            chalk.dim('\n  ' + formatPagination(result.offset || 0, result.limit || 10, result.total || 0, true))
          );
        }
        if (isInteractiveOutput()) console.log('');
      } else {
        console.log(chalk.dim('\n  No projects found\n'));
      }
    }
  } catch (error) {
    handleApiError(error);
  }
}

export async function listSections(
  projectId: string | number,
  token?: string,
  apiUrl?: string,
  json?: boolean
): Promise<void> {
  try {
    const service = await createProjectService(token, apiUrl);
    const result = await service.listSections({
      project_id: parseNumber(projectId.toString(), 'project'),
    });

    if (json) {
      console.log(formatOutput(result, true));
    } else {
      if (isInteractiveOutput()) {
        console.log('');
        console.log(chalk.bold.white(`  Sections in project ${chalk.cyan(String(projectId))}`));
      }
      if (result.sections && result.sections.length > 0) {
        const rows = result.sections.map((s: any) => {
          const title = s.section_title ?? s.title;
          return [s.id, title ?? '-', s.taskCount || 0];
        });
        console.log(formatTable(['ID', 'Title', 'Tasks'], rows));
      } else {
        console.log(chalk.dim('  No sections found'));
      }
      if (isInteractiveOutput()) console.log('');
    }
  } catch (error) {
    handleApiError(error);
  }
}

export async function createSection(
  projectId: number,
  title: string,
  afterSectionId: number | undefined,
  token?: string,
  apiUrl?: string,
  json?: boolean
): Promise<void> {
  try {
    const service = await createSectionService(token, apiUrl);
    const result = await service.manageSection({
      action: 'create',
      project_id: projectId,
      title,
      after_section_id: afterSectionId,
    });

    const createResult = result as { section: { id: number; section_title: string }; message?: string };
    if (json) {
      console.log(formatOutput(result, true));
    } else {
      console.log('');
      console.log(`  ${chalk.green.bold('✓ Section created')}`);
      console.log(chalk.dim('  ' + '─'.repeat(40)));
      console.log(`  ${chalk.dim('Name')}      ${chalk.bold.white(createResult.section.section_title)}`);
      console.log(`  ${chalk.dim('ID')}        ${chalk.cyan(String(createResult.section.id))}`);
      console.log(`  ${chalk.dim('Project')}   ${chalk.dim(String(projectId))}`);
      console.log('');
    }
  } catch (error) {
    handleApiError(error);
  }
}

export async function updateSection(
  projectId: number,
  sectionId: number,
  title: string,
  token?: string,
  apiUrl?: string,
  json?: boolean
): Promise<void> {
  try {
    const service = await createSectionService(token, apiUrl);
    const result = await service.manageSection({
      action: 'update',
      project_id: projectId,
      title,
      section_id: sectionId,
    });

    const updateResult = result as { section: { id: number; section_title: string }; message?: string };
    if (json) {
      console.log(formatOutput(result, true));
    } else {
      console.log('');
      console.log(`  ${chalk.green.bold('✓ Section updated')}`);
      console.log(chalk.dim('  ' + '─'.repeat(40)));
      console.log(`  ${chalk.dim('Name')}  ${chalk.bold.white(updateResult.section.section_title)}`);
      console.log(`  ${chalk.dim('ID')}    ${chalk.cyan(String(updateResult.section.id))}`);
      console.log('');
    }
  } catch (error) {
    handleApiError(error);
  }
}

function resolveSectionReference(
  sectionValue: string,
  sections: SectionListEntry[]
): SectionListEntry | undefined {
  const sectionVal = sectionValue.trim();
  const sectionIdNum = parseInt(sectionVal, 10);
  if (!isNaN(sectionIdNum) && String(sectionIdNum) === sectionVal) {
    return sections.find((s) => s.id === sectionIdNum);
  }
  return sections.find((s) => s.section_title.toLowerCase() === sectionVal.toLowerCase());
}

function formatAvailableSections(sections: SectionListEntry[]): string {
  return sections.map((s) => `${s.section_title} (${s.id})`).join(', ');
}

export async function reorderSection(
  projectId: number,
  section: string,
  after: string,
  token?: string,
  apiUrl?: string,
  json?: boolean
): Promise<void> {
  try {
    const service = await createSectionService(token, apiUrl);
    const sectionsResult = await service.manageSection({
      action: 'list',
      project_id: projectId,
    });
    const sections = ((sectionsResult as any).sections || []) as SectionListEntry[];
    const targetSection = resolveSectionReference(section, sections);
    const afterSection = resolveSectionReference(after, sections);
    const availableSections = formatAvailableSections(sections);

    if (!targetSection) {
      throw new Error(`Section "${section}" not found. Available: ${availableSections}`);
    }
    if (!afterSection) {
      throw new Error(`Section "${after}" not found. Available: ${availableSections}`);
    }
    if (targetSection.id === afterSection.id) {
      throw new Error('Section cannot be moved after itself');
    }

    const result = await service.manageSection({
      action: 'update',
      project_id: projectId,
      section_id: targetSection.id,
      move_after_section_id: afterSection.id,
    });

    const reorderResult = result as { section: { id: number; section_title: string }; message?: string };
    if (json) {
      console.log(formatOutput(result, true));
    } else {
      console.log('');
      console.log(`  ${chalk.green.bold('✓ Section reordered')}`);
      console.log(chalk.dim('  ' + '─'.repeat(40)));
      console.log(`  ${chalk.dim('Name')}  ${chalk.bold.white(reorderResult.section.section_title)}`);
      console.log(`  ${chalk.dim('ID')}    ${chalk.cyan(String(reorderResult.section.id))}`);
      console.log(`  ${chalk.dim('After')} ${chalk.cyan(String(afterSection.id))} ${chalk.dim(afterSection.section_title)}`);
      console.log('');
    }
  } catch (error) {
    handleApiError(error);
  }
}

export async function deleteSection(
  projectId: number,
  sectionId: number,
  token?: string,
  apiUrl?: string,
  json?: boolean
): Promise<void> {
  try {
    const service = await createSectionService(token, apiUrl);
    const result = await service.manageSection({
      action: 'delete',
      project_id: projectId,
      section_id: sectionId,
    });

    const deleteResult = result as { section: { id: number; section_title: string }; message?: string };
    if (json) {
      console.log(formatOutput(result, true));
    } else {
      console.log(formatSuccess(`${deleteResult.message}`));
    }
  } catch (error) {
    handleApiError(error);
  }
}


export async function listMembers(
  projectId: string | number,
  token?: string,
  apiUrl?: string,
  json?: boolean
): Promise<void> {
  try {
    const requestedId = parseNumber(projectId.toString(), 'project');
    const service = await createProjectService(token, apiUrl);
    const result = await service.listProjectMembers({
      project_id: requestedId,
    });

    if (json) {
      console.log(formatOutput(result, true));
    } else {
      if (isInteractiveOutput()) {
        console.log('');
        console.log(chalk.bold.white(`  Members of project ${chalk.cyan(String(projectId))}`));
      }
      if (result.members && result.members.length > 0) {
        const rows = result.members.map((m) => [
          isProjectAgent(m) ? 'agent' : 'user',
          m.displayName,
          isProjectAgent(m) ? m.id : m.email,
        ]);
        console.log(formatTable(['Type', 'Name', 'ID/Email'], rows));
      } else {
        console.log(chalk.dim('  No members found'));
      }
      if (isInteractiveOutput()) console.log('');
    }
  } catch (error) {
    handleApiError(error);
  }
}

export async function addMember(
  projectId: string | number,
  user?: string | number,
  token?: string,
  apiUrl?: string,
  json?: boolean
): Promise<void> {
  try {
    const requestedId = parseNumber(projectId.toString(), 'project');
    const service = await createProjectService(token, apiUrl);
    const result = await service.addProjectMember({
      project_id: requestedId,
      user,
    });

    if (json) {
      console.log(formatOutput(result, true));
    } else {
      console.log(result)
    }
  } catch (error) {
    handleApiError(error);
  }
}

/** UUID pattern - labels use UUID format */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Check if a string looks like a label ID (UUID or numeric) vs a name */
function isLabelId(value: string): boolean {
  const t = value.trim();
  return UUID_PATTERN.test(t) || /^\d+$/.test(t);
}

/** Normalize label name for matching: strip emojis/symbols, trim, lowercase */
function normalizeLabelName(name: string): string {
  return name
    .replace(/[^\w\s-]/g, '') // Remove emojis, symbols (keeps letters, digits, space, hyphen)
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * Find best matching label for user input.
 * Handles emojis (e.g. "BUG" matches "BUG 🪲"), case-insensitivity, and partial matches.
 */
function findBestLabelMatch(
  item: string,
  projectLabels: Array<{ id: string; name: string; color?: string }>
): { id: string; name: string; color?: string } | null {
  const itemNorm = item.trim().toLowerCase();
  if (!itemNorm) return null;

  // 1. Exact match (full label, case-insensitive)
  const exact = projectLabels.find((l) => l.name.toLowerCase() === itemNorm);
  if (exact) return exact;

  // 2. Normalized exact match: "BUG" matches "BUG 🪲" (strip emojis)
  const normalizedExact = projectLabels.find(
    (l) => normalizeLabelName(l.name) === itemNorm
  );
  if (normalizedExact) return normalizedExact;

  // 3. Normalized starts-with: "bug" matches "BUG 🪲" or "bugs"
  const startsWithMatches = projectLabels.filter((l) =>
    normalizeLabelName(l.name).startsWith(itemNorm)
  );
  if (startsWithMatches.length === 1) return startsWithMatches[0];
  if (startsWithMatches.length > 1) {
    // Prefer the one where normalized label equals item (exact word match)
    const exactWord = startsWithMatches.find(
      (l) => normalizeLabelName(l.name) === itemNorm
    );
    if (exactWord) return exactWord;
    // Otherwise prefer shortest (most specific)
    startsWithMatches.sort(
      (a, b) =>
        normalizeLabelName(a.name).length - normalizeLabelName(b.name).length
    );
    return startsWithMatches[0];
  }

  // 4. Normalized includes: "CLI" matches "CLI" (exact) or "MCP, CLI" etc
  const includesMatch = projectLabels.find((l) =>
    normalizeLabelName(l.name).includes(itemNorm)
  );
  if (includesMatch) return includesMatch;

  return null;
}

/**
 * Get project labels as array (for internal use).
 * Returns [{ id, name, color? }, ...]
 */
export async function getProjectLabels(
  projectId: number,
  token?: string,
  apiUrl?: string
): Promise<Array<{ id: string; name: string; color?: string }>> {
  const service = await createProjectService(token, apiUrl);
  const projectsResult = await service.listProjects({ limit: 100, offset: 0 });
  const project = projectsResult.projects?.find((p: any) => p.id === projectId);
  if (!project || !project.labels) return [];
  return project.labels;
}

/**
 * Resolve label names/IDs to IDs only.
 * Accepts comma-separated list; names are resolved via project labels, IDs pass through.
 */
export async function resolveLabelsToIds(
  labelsInput: string,
  projectId: number,
  token?: string,
  apiUrl?: string
): Promise<(string | number)[]> {
  const items = labelsInput.split(',').map((s) => s.trim()).filter(Boolean);
  if (items.length === 0) return [];

  const projectLabels = await getProjectLabels(projectId, token, apiUrl);
  const resolved: (string | number)[] = [];

  for (const item of items) {
    if (isLabelId(item)) {
      resolved.push(/^\d+$/.test(item) ? parseInt(item, 10) : item);
    } else {
      const match = findBestLabelMatch(item, projectLabels);
      if (!match) {
        const available = projectLabels.map((l) => l.name).join(', ') || 'none';
        throw new Error(
          `Label "${item}" not found in project ${projectId}. Available: ${available}. ` +
            `Create it first with: hypertask project label create --project ${projectId} --name "${item}"`
        );
      }
      resolved.push(match.id);
    }
  }

  return resolved;
}

/**
 * Resolve `--label` filter for task list: comma-separated names or IDs.
 * ID-only input does not require projectId. Any label name requires projectId to resolve.
 */
export async function resolveLabelsForListFilter(
  labelsInput: string,
  projectId: number | undefined,
  token?: string,
  apiUrl?: string
): Promise<(string | number)[]> {
  const items = labelsInput.split(',').map((s) => s.trim()).filter(Boolean);
  if (items.length === 0) return [];

  const allIds = items.every((item) => isLabelId(item));
  if (allIds) {
    return items.map((item) => (/^\d+$/.test(item.trim()) ? parseInt(item, 10) : item.trim()));
  }

  if (projectId === undefined) {
    throw new Error(
      'Label names require --project so labels can be resolved. Use label UUIDs or numeric IDs with --label, or pass --project together with --label.'
    );
  }

  return resolveLabelsToIds(labelsInput, projectId, token, apiUrl);
}

export async function createLabel(
  projectId: number,
  name: string,
  token?: string,
  apiUrl?: string,
  json?: boolean
): Promise<void> {
  try {
    const service = await createProjectService(token, apiUrl);
    const params = { project_id: projectId, name };

    const result = await service.createLabel(params);

    if (json) {
      console.log(formatOutput(result, true));
    } else {
      console.log(formatSuccess(`Created label "${result.label.name}" (${result.label.id}) in project ${projectId}`));
    }
  } catch (error) {
    handleApiError(error);
  }
}

export async function listLabels(
  projectId: string | number,
  token?: string,
  apiUrl?: string,
  json?: boolean
): Promise<void> {
  try {
    const service = await createProjectService(token, apiUrl);
    // Get project details which includes labels (limit 100 is max allowed)
    const projectsResult = await service.listProjects({ limit: 100, offset: 0 });
    const project = projectsResult.projects?.find((p: any) => p.id === parseNumber(projectId.toString(), 'project'));

    if (!project) {
      throw new Error(`Project ${projectId} not found`);
    }

    if (json) {
      console.log(formatOutput({ success: true, labels: project.labels || [] }, true));
    } else {
      if (isInteractiveOutput()) {
        console.log('');
        console.log(chalk.bold.white(`  Labels in project ${chalk.cyan(String(projectId))}  ${chalk.dim(project.title)}`));
      }
      if (project.labels && project.labels.length > 0) {
        const rows = project.labels.map((label: any) => [
          label.id,
          label.name,
          label.color ?? '',
        ]);
        console.log(formatTable(['ID', 'Name', 'Color'], rows));
      } else {
        console.log(chalk.dim('  No labels available'));
      }
      if (isInteractiveOutput()) console.log('');
    }
  } catch (error) {
    handleApiError(error);
  }
}
